/* ============================================================
   V5 测评页 —— 真后端 V5 出题接口驱动（M4 serving）
   ------------------------------------------------------------
   后端契约（apps/assessment/v5_serving.py）：
     · GET  /assessments/{id}/current-question/  逐题下发当前题
     · POST /assessments/{id}/answer/            提交单题 → 推进
   返回字段：phase/round_number/order_no/total_questions/total_rounds/
            answered_count/question{id,stem,options,dimension,trait}
   选项脱敏（无 main_score）。AI 题预生成中返回 status='generating'，
   前端 setTimeout 2s 重拉；status='completed' 跳报告页。
   ============================================================ */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentQuestion, submitAnswer } from '../api/assessment';
import { useAuth } from '../context/AuthContext';
import EngineerPanel from '../components/EngineerPanel';
import './AssessmentPage.css';

const pad2 = (n) => String(n).padStart(2, '0');
const clock = (sec) => `${pad2(Math.floor(sec / 60))}:${pad2(sec % 60)}`;

export default function AssessmentPage() {
  const navigate = useNavigate();
  const { id: assessmentId } = useParams();
  const { user } = useAuth();
  const engineerMode = !!user?.is_engineer_mode;

  const [serverData, setServerData] = useState(null);    // 后端响应整包
  const [selectedLetter, setSelectedLetter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showTransition, setShowTransition] = useState(false);
  const [pendingTraitData, setPendingTraitData] = useState(null);

  const prevPhaseRef = useRef(null);                     // 上一题 phase（检测 ability→trait 切换）
  const questionStartRef = useRef(Date.now());
  const generatingTimerRef = useRef(null);

  // ---- 拉当前题 ----
  const fetchCurrent = useCallback(async () => {
    setError('');
    try {
      const { data } = await getCurrentQuestion(assessmentId);
      if (data.status === 'completed') {
        setDone(true);
        return;
      }
      const prev = prevPhaseRef.current;
      if (data.status === 'generating') {
        // 能力 → 素质过渡期 AI 还在出题：弹 TransitionCard 吸收等待，避免全屏 LoadingCard
        // 割裂感。用户读过渡文案那 5-10 秒里素质题通常已 ready；过快用户停在过渡卡上
        // 等按钮恢复点击即可（界面不切换）。
        if (prev === 'ability' && data.phase === 'trait') {
          setPendingTraitData(null);     // 还没 ok，先标记进过渡态
          setShowTransition(true);
        }
        setServerData(data);
        if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
        generatingTimerRef.current = setTimeout(fetchCurrent, 2000);
        return;
      }
      // status === 'ok' —— 检测 ability → trait 过渡
      // 这一分支也覆盖「用户停在 TransitionCard 等 trait 题 ready 完成」的情况：
      // prev 在 enterTraitPhase 触发前一直是 'ability'，所以 ready 后这里会再次
      // 命中，pendingTraitData 被填上，按钮恢复可点。
      if (prev === 'ability' && data.phase === 'trait') {
        setPendingTraitData(data);
        setShowTransition(true);
        return;
      }
      prevPhaseRef.current = data.phase;
      setServerData(data);
      setSelectedLetter('');
      questionStartRef.current = Date.now();
    } catch (err) {
      setError(err.response?.data?.detail || '加载题目失败');
    }
  }, [assessmentId]);

  // 首次加载 + 卸载清理
  useEffect(() => {
    fetchCurrent();
    return () => {
      if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
    };
  }, [fetchCurrent]);

  // 计时
  useEffect(() => {
    if (done) return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  // ---- 提交单题 ----
  const submit = useCallback(async (special) => {
    const q = serverData?.question;
    if (!q?.id || isSubmitting) return;
    if (!special && !selectedLetter) return;
    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        question_id: q.id,
        time_spent_ms: Date.now() - questionStartRef.current,
        selected_options: special ? [] : [selectedLetter],
        special_option: special || 'none',
      };
      const { data } = await submitAnswer(assessmentId, payload);
      if (data.status === 'completed') {
        setDone(true);
        return;
      }
      await fetchCurrent();
    } catch (err) {
      setError(err.response?.data?.detail || '提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  }, [assessmentId, selectedLetter, serverData, isSubmitting, fetchCurrent]);

  // 键盘：A–D 选择，Enter 提交
  useEffect(() => {
    const q = serverData?.question;
    if (showTransition || done || !q || serverData?.status !== 'ok') return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      const k = e.key.toUpperCase();
      if (['A', 'B', 'C', 'D'].includes(k)) {
        if (q.options.find((o) => o.letter === k)) setSelectedLetter(k);
      } else if (e.key === 'Enter' && selectedLetter && !isSubmitting) {
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [serverData, selectedLetter, isSubmitting, showTransition, done, submit]);

  const enterTraitPhase = () => {
    if (!pendingTraitData) return;
    prevPhaseRef.current = pendingTraitData.phase;
    setServerData(pendingTraitData);
    setShowTransition(false);
    setPendingTraitData(null);
    setSelectedLetter('');
    questionStartRef.current = Date.now();
  };

  // ---- render ----
  if (done) {
    return (
      <CompletionPage
        total={serverData?.total_questions || 0}
        elapsed={elapsed}
        onReport={() => navigate(`/report/v5/${assessmentId}`)}
        onHome={() => navigate('/')}
      />
    );
  }

  const q = serverData?.question;
  const isGenerating = serverData?.status === 'generating';
  const isReady = serverData?.status === 'ok' && q;
  const total = serverData?.total_questions || 0;
  const totalRounds = serverData?.total_rounds || 1;
  const answeredCount = serverData?.answered_count || 0;
  const orderNo = serverData?.order_no ?? 0;
  const phase = serverData?.phase || 'ability';
  const roundNumber = serverData?.round_number || 1;
  const percent = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  const groupLabel = (() => {
    if (!q) return null;
    if (q.dimension) return { kind: 'ability', no: q.dimension.sort_order, name: q.dimension.name };
    if (q.trait) return { kind: 'trait', no: q.trait.sort_order, name: q.trait.name };
    return null;
  })();

  return (
    <div className="v5-assessment">
      {/* ===== slim header ===== */}
      <header className="va-topbar">
        <div className="va-brand">
          <span className="va-logo">AIQ</span>
          <span className="va-brand-tag">AI 胜任力测评</span>
        </div>
        <nav className="va-nav">
          <button type="button" onClick={() => navigate('/')}>模型</button>
          <button type="button" className="active">测评</button>
          <button type="button" className="disabled" disabled>报告</button>
        </nav>
        <div className="va-topbar-right">
          <span className="va-saved">进度已自动保存</span>
          <button type="button" className="va-btn ghost sm" onClick={() => navigate('/')}>
            保存并退出
          </button>
        </div>
      </header>

      {/* ===== 进度条（线性，phase + round + 百分比） ===== */}
      <div className="va-progress">
        <div className="va-progress-inner">
          <div className="va-progress-meta">
            <span className="now">{pad2(Math.min(answeredCount + 1, total))}</span>
            <span className="total">/ {total} 题</span>
          </div>
          <div className="va-segbar">
            <div className="va-seg cur" style={{ flex: 1 }}>
              <div className="va-seg-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
          <div className="va-progress-pct">{percent}%</div>
        </div>
      </div>

      {/* ===== 主体 ===== */}
      <main className="va-stage">
        {showTransition ? (
          <TransitionCard
            onContinue={enterTraitPhase}
            isLoading={!pendingTraitData}
          />
        ) : error && !isReady ? (
          <LoadingCard message={error} onRetry={fetchCurrent} />
        ) : isGenerating ? (
          <LoadingCard message="AI 正在根据您的作答情况调整后续题目，请耐心等待" />
        ) : !isReady ? (
          <LoadingCard message="加载中…" />
        ) : (
          <>
            <article className="va-qcard">
              <div className="va-qhead">
                <span className="va-pill">
                  {groupLabel?.kind === 'ability' ? (
                    <>
                      <span className="num">N° {pad2(groupLabel.no)}</span> · {groupLabel.name}
                    </>
                  ) : groupLabel?.kind === 'trait' ? (
                    <>
                      <span className="num">素质 {pad2(groupLabel.no)}</span> · {groupLabel.name}
                    </>
                  ) : (
                    <span className="num">第 {pad2(roundNumber)} 轮</span>
                  )}
                </span>
              </div>

              <h1 className="va-qtitle">
                {[q.scenario_text, q.stem].filter(Boolean).join(' ')}
              </h1>

              <div className="va-options">
                {q.options.map((opt) => {
                  const sel = selectedLetter === opt.letter;
                  return (
                    <button
                      type="button"
                      key={opt.letter}
                      className={`va-opt${sel ? ' selected' : ''}`}
                      onClick={() => setSelectedLetter(opt.letter)}
                      disabled={isSubmitting}
                    >
                      <span className="va-opt-key">{opt.letter}</span>
                      <span className="va-opt-text">
                        <b>{opt.text}</b>
                      </span>
                      <span className="va-opt-check" />
                    </button>
                  );
                })}
              </div>

              {error && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 8,
                  background: '#fdecec', color: '#b42318', fontSize: 13.5,
                }}>{error}</div>
              )}

              <div className="va-qnav">
                <div className="va-qnav-left">
                  <button type="button" className="va-btn ghost" disabled
                          title="V5 单题作答提交后不可回退">
                    ← 上一题
                  </button>
                  <button type="button" className="va-btn ghost"
                          onClick={() => submit('skipped')} disabled={isSubmitting}>
                    跳过
                  </button>
                </div>
                <div className="va-qnav-right">
                  <span className="va-hint">
                    快捷键 <kbd>A–D</kbd> 选择 · <kbd>↵</kbd> 下一题
                  </span>
                  <button
                    type="button"
                    className="va-btn primary"
                    onClick={() => submit()}
                    disabled={!selectedLetter || isSubmitting}
                  >
                    {answeredCount + 1 >= total ? '完成测评 →' : '下一题 →'}
                  </button>
                </div>
              </div>
            </article>

            {/* 侧栏 */}
            <aside className="va-side">
              <div className="va-side-card">
                <h5>
                  本次进度
                  <span className="mono">{pad2(Math.min(orderNo + 1, total))} / {total}</span>
                </h5>
                <div className="va-navmap">
                  {Array.from({ length: total }).map((_, i) => {
                    let cls = 'todo';
                    if (i < answeredCount) cls = 'done';
                    else if (i === orderNo) cls = 'cur';
                    return (
                      <span
                        key={i}
                        className={`va-nm-cell ${cls}`}
                        title={`第 ${i + 1} 题`}
                      >
                        {pad2(i + 1)}
                      </span>
                    );
                  })}
                </div>
                <div className="va-nm-legend">
                  <div className="row"><span className="sw cur" />当前</div>
                  <div className="row"><span className="sw done" />已完成</div>
                  <div className="row"><span className="sw todo" />未作答</div>
                </div>
              </div>

              <div className="va-side-card">
                <h5>本次会话 <span className="mono">SESSION</span></h5>
                <div className="va-meta-list">
                  <div className="it"><span className="k">已用时间</span><span className="v">{clock(elapsed)}</span></div>
                  <div className="it"><span className="k">阶段</span>
                    <span className="v">{phase === 'ability' ? '能力测评' : '素质测评'}</span>
                  </div>
                  <div className="it"><span className="k">当前轮</span>
                    <span className="v">{roundNumber}{phase === 'ability' ? ` / ${totalRounds}` : ''}</span>
                  </div>
                </div>
                <p className="va-side-note">
                  {phase === 'ability'
                    ? `能力阶段共 ${totalRounds} 轮，每轮 8 题；可随时退出，进度自动保存。`
                    : '素质阶段没有标准答案，凭真实情况作答即可。'}
                </p>
              </div>

              {/* 工程模式：超管自查题目调度链路（普通用户拿不到 engineer_info） */}
              {engineerMode && q?.engineer_info && (
                <EngineerPanel info={q.engineer_info} />
              )}
            </aside>
          </>
        )}
      </main>
    </div>
  );
}

/** 能力题 → 素质题 阶段过渡提示（§7.2 偏差 3）
 *
 * isLoading=true 时素质题还在 AI 预生成中：按钮 disable + 内联 loader 文案，
 * 不替换页面。轮询命中 ready 后自动恢复点击。这样替代了原先的全屏 LoadingCard
 * 弹窗（消除用户感知的「割裂感」）。
 */
function TransitionCard({ onContinue, isLoading }) {
  return (
    <div className="va-transition">
      <div className="va-transition-card">
        <div className="va-transition-mark">✓</div>
        <div className="va-transition-kicker">能力测评部分已完成</div>
        <h2>接下来，了解你的 AI 使用风格</h2>
        <p>
          接下来的题目用于了解你在意愿、思考和学习方面的 AI 使用习惯。
          这部分<b>没有标准答案</b>，凭你的真实情况作答即可。
        </p>
        <button
          type="button"
          className="va-btn primary lg"
          onClick={onContinue}
          disabled={isLoading}
        >
          {isLoading ? '正在为你准备题目…' : '继续 →'}
        </button>
      </div>
    </div>
  );
}

function LoadingCard({ message, onRetry }) {
  return (
    <div className="va-transition">
      <div className="va-transition-card">
        <div className="va-transition-kicker">LOADING</div>
        <h2 style={{ fontSize: 22 }}>{message}</h2>
        {onRetry && (
          <button type="button" className="va-btn primary lg" onClick={onRetry}
                  style={{ marginTop: 12 }}>
            重试
          </button>
        )}
      </div>
    </div>
  );
}

function CompletionPage({ total, elapsed, onReport, onHome }) {
  return (
    <div className="v5-assessment">
      <main className="va-stage">
        <div className="va-transition">
          <div className="va-transition-card">
            <div className="va-transition-mark done">★</div>
            <div className="va-transition-kicker">ASSESSMENT COMPLETE</div>
            <h2>测评完成</h2>
            <p>你已完成全部 {total} 道题目，报告将在后台异步生成（通常 1-2 分钟）。</p>
            <div className="va-complete-stats">
              <div className="stat"><span className="num">{total}</span><span className="lab">题目总数</span></div>
              <div className="stat"><span className="num">{clock(elapsed)}</span><span className="lab">总用时</span></div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="va-btn primary lg" onClick={onReport}>
                查看报告 →
              </button>
              <button type="button" className="va-btn ghost lg" onClick={onHome}>
                返回首页
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
